import { apiGet, apiPost } from './client.js';

export type BalanceCleanupRow = {
  id: string;
  ticketNumber: string;
  status: string;
  createdAt: string;
  closedAt: string | null;
  total: string;
  paidTotal: string;
  balanceDue: string;
  balanceWrittenOff: string;
  paymentRows: number;
  importedFrom: string | null;
  externalId: string | null;
  customerName: string;
};

export type BalanceCleanupMeta = {
  page: number;
  pageSize: number;
  total: number;
  balanceSum: string;
  cancelledCount: number;
};

export type CleanupBatch = {
  id: string;
  operation: string;
  ticketCount: number;
  totalAmount: string | null;
  reason: string | null;
  performedBy: string | null;
  performedAt: string;
  reversedAt: string | null;
  reversedBy: string | null;
};

export type SmartPreview = {
  ticketCount: number;
  totalAmount: string;
  tickets: Array<{
    id: string;
    ticketNumber: string;
    balanceDue: string;
    status: string;
    paymentRows: number;
  }>;
};

export type ListBalancesParams = {
  statuses?: string[];
  hasPayments?: 'yes' | 'no' | 'any';
  createdFrom?: string;
  createdTo?: string;
  minBalance?: number;
  maxBalance?: number;
  customerSearch?: string;
  importedOnly?: boolean;
  page?: number;
  pageSize?: number;
};

export function listBalanceCleanupTickets(params: ListBalancesParams = {}) {
  const qs = new URLSearchParams();
  if (params.statuses?.length) qs.set('statuses', params.statuses.join(','));
  if (params.hasPayments) qs.set('hasPayments', params.hasPayments);
  if (params.createdFrom) qs.set('createdFrom', params.createdFrom);
  if (params.createdTo) qs.set('createdTo', params.createdTo);
  if (params.minBalance != null) qs.set('minBalance', String(params.minBalance));
  if (params.maxBalance != null) qs.set('maxBalance', String(params.maxBalance));
  if (params.customerSearch) qs.set('customerSearch', params.customerSearch);
  if (params.importedOnly) qs.set('importedOnly', 'true');
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return apiGet<BalanceCleanupRow[]>(`/api/v1/data-cleanup/balances${q ? `?${q}` : ''}`).then(
    (r) => ({ data: r.data, meta: r.meta as BalanceCleanupMeta }),
  );
}

export function listCleanupBatches() {
  return apiGet<CleanupBatch[]>('/api/v1/data-cleanup/balances/batches').then((r) => r.data);
}

export function previewSmartCleanupRule(rule: 'cancelled_no_payments' | 'cancelled_with_payments') {
  return apiGet<SmartPreview>(`/api/v1/data-cleanup/balances/smart-preview/${rule}`).then(
    (r) => r.data,
  );
}

export function markPaidCleanup(body: {
  ticketIds: string[];
  method: string;
  paidAt?: string | null;
  useClosedAt?: boolean;
  reason?: string | null;
  amounts?: Record<string, number>;
  managerOverrideAuthorizedBy: string;
  acknowledgeClosedSession?: boolean;
}) {
  return apiPost<{
    batchId: string;
    ticketCount: number;
    totalAmount: string;
    closedSessionWarnings: string[];
  }>('/api/v1/data-cleanup/balances/mark-paid', body).then((r) => r.data);
}

export function writeOffCleanup(body: {
  ticketIds: string[];
  reason: string;
  managerOverrideAuthorizedBy: string;
}) {
  return apiPost<{ batchId: string; ticketCount: number; totalAmount: string }>(
    '/api/v1/data-cleanup/balances/write-off',
    body,
  ).then((r) => r.data);
}

export function cancelVoidCleanup(body: {
  ticketIds: string[];
  status: 'cancelled' | 'voided';
  managerOverrideAuthorizedBy: string;
}) {
  return apiPost<{ batchId: string; ticketCount: number; totalAmount: string }>(
    '/api/v1/data-cleanup/balances/cancel-void',
    body,
  ).then((r) => r.data);
}

export function recalculateCleanup(body: {
  ticketIds: string[];
  managerOverrideAuthorizedBy: string;
}) {
  return apiPost<{ batchId: string; ticketCount: number }>(
    '/api/v1/data-cleanup/balances/recalculate',
    body,
  ).then((r) => r.data);
}

export function applySmartCleanupRule(
  rule: 'cancelled_no_payments' | 'cancelled_with_payments',
  body: { managerOverrideAuthorizedBy: string },
) {
  return apiPost<{ batchId?: string; ticketCount: number; totalAmount?: string }>(
    `/api/v1/data-cleanup/balances/smart/${rule}`,
    body,
  ).then((r) => r.data);
}

export function undoCleanupBatch(id: string, managerOverrideAuthorizedBy: string) {
  return apiPost(`/api/v1/data-cleanup/balances/batches/${id}/undo`, {
    managerOverrideAuthorizedBy,
  }).then((r) => r.data);
}

/* ── Parts cost backfill ───────────────────────────────────────────── */

export type PartsCostRow = {
  ticketId: string;
  ticketRepairId: string;
  ticketNumber: string;
  createdAt: string;
  status: string;
  deviceLabel: string;
  makeId: string | null;
  modelId: string | null;
  serviceName: string;
  existingParts: Array<{
    id: string;
    partName: string;
    unitCost: string;
    totalCost: string;
    costSource: string;
    entryMethod: string;
  }>;
  hasCost: boolean;
};

export type PartsCostMeta = {
  page: number;
  pageSize: number;
  total: number;
  completedTickets: number;
  totalTickets: number;
  costsRecorded: string;
};

export type PartsCostSuggestion = {
  partName: string;
  unitCost: string;
  inventoryItemId: string | null;
  source: 'inventory' | 'prior_backfill';
};

export type ListPartsCostParams = {
  createdFrom?: string;
  createdTo?: string;
  makeId?: string;
  modelId?: string;
  serviceName?: string;
  hasParts?: 'yes' | 'no' | 'any';
  statuses?: string[];
  hideCompleted?: boolean;
  groupByService?: boolean;
  page?: number;
  pageSize?: number;
};

export function listPartsCostRows(params: ListPartsCostParams = {}) {
  const qs = new URLSearchParams();
  if (params.createdFrom) qs.set('createdFrom', params.createdFrom);
  if (params.createdTo) qs.set('createdTo', params.createdTo);
  if (params.makeId) qs.set('makeId', params.makeId);
  if (params.modelId) qs.set('modelId', params.modelId);
  if (params.serviceName) qs.set('serviceName', params.serviceName);
  if (params.hasParts) qs.set('hasParts', params.hasParts);
  if (params.statuses?.length) qs.set('statuses', params.statuses.join(','));
  if (params.hideCompleted) qs.set('hideCompleted', 'true');
  if (params.groupByService === false) qs.set('groupByService', 'false');
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const q = qs.toString();
  return apiGet<PartsCostRow[]>(`/api/v1/data-cleanup/parts-costs${q ? `?${q}` : ''}`).then(
    (r) => ({ data: r.data, meta: r.meta as PartsCostMeta }),
  );
}

export function autocompletePartsCost(q: string) {
  return apiGet<PartsCostSuggestion[]>(
    `/api/v1/data-cleanup/parts-costs/autocomplete?q=${encodeURIComponent(q)}`,
  ).then((r) => r.data);
}

export function listPartsCostServices() {
  return apiGet<string[]>('/api/v1/data-cleanup/parts-costs/services').then((r) => r.data);
}

export function listPartsCostBatches() {
  return apiGet<CleanupBatch[]>('/api/v1/data-cleanup/parts-costs/batches').then((r) => r.data);
}

export function saveExactPartsCost(body: {
  ticketRepairId: string;
  parts: Array<{
    partName?: string | null;
    unitCost: number;
    quantity?: number;
    inventoryItemId?: string | null;
  }>;
  overwriteExisting?: boolean;
  managerOverrideAuthorizedBy: string;
}) {
  return apiPost<{ batchId: string; partIds: string[]; amount: string }>(
    '/api/v1/data-cleanup/parts-costs/exact',
    body,
  ).then((r) => r.data);
}

export function previewBulkPartsCost(body: {
  serviceName: string;
  makeId?: string | null;
  modelId?: string | null;
  createdFrom: string;
  createdTo: string;
  unitCost: number;
  overwriteExisting?: boolean;
}) {
  return apiPost<{
    matchCount: number;
    totalCost: string;
    tickets: Array<{
      ticketId: string;
      ticketRepairId: string;
      ticketNumber: string;
      createdAt: string;
      serviceName: string;
      deviceLabel: string;
      hasExistingParts: boolean;
    }>;
  }>('/api/v1/data-cleanup/parts-costs/bulk/preview', body).then((r) => r.data);
}

export function applyBulkPartsCost(body: {
  serviceName: string;
  makeId?: string | null;
  modelId?: string | null;
  createdFrom: string;
  createdTo: string;
  partName?: string | null;
  unitCost: number;
  inventoryItemId?: string | null;
  overwriteExisting?: boolean;
  managerOverrideAuthorizedBy: string;
}) {
  return apiPost<{
    batchId?: string;
    ticketCount: number;
    repairCount?: number;
    totalAmount: string;
  }>('/api/v1/data-cleanup/parts-costs/bulk', body).then((r) => r.data);
}

export function undoPartsCostBatch(id: string, managerOverrideAuthorizedBy: string) {
  return apiPost(`/api/v1/data-cleanup/parts-costs/batches/${id}/undo`, {
    managerOverrideAuthorizedBy,
  }).then((r) => r.data);
}
